import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs/internal/Subject';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { environment } from '@environment';
import { TreeModel, TreeModule, TreeNode } from '@ali-hm/angular-tree-component';
import { NgClass } from '@angular/common';
import { InlineSVGComponent } from '../../../Frontend-Inline-SVG-Module/inline-svg.component';
import { TruncatePipe } from '@sb-tools/typescript/pipes/truncate.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { FocusDirective } from '@sb-tools/typescript/directives/focus.directive';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
    selector: 'app-categories-modal',
    templateUrl: './categories-modal.component.html',
    styleUrls: ['./categories-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        InlineSVGComponent,
        TruncatePipe,
        TranslateModule,
        FormsModule,
        FocusDirective,
        TreeModule,
        TooltipModule,
    ],
})
export class CategoriesModalComponent implements OnInit, OnDestroy {
    alertMessage;
    communityName: string;
    isAdmin: boolean;
    isIgnoreUnselected: boolean;
    isPrivate: boolean;
    modalId? = null; // if modal is nested, need to pass id to close particular this modal
    nodes = [];
    onChangeCategory;
    onClose?: () => void;
    isDisableRemoving = false;
    ignoreDeactivate = false;
    redirectToCategoriesUrl: string;
    searchQuery: string;
    searchQueryUpdate = new Subject<string>();
    selectedCategoryIds: number[] = [];
    treeModel;

    private unsubscriber = new Subject<void>();

    constructor(
        public modalRef: BsModalRef,
        private modalService: BsModalService,
        private commonDataService: CommonDataService,
    ) {}

    ngOnInit() {
        if (this.isPrivate) {
            this.nodes = this.commonDataService.categories.private;
        } else {
            this.nodes = this.commonDataService.categories.all;
        }

        this.isAdmin = this.commonDataService.user.isAdmin;
        this.communityName = this.commonDataService.communityName;
        this.redirectToCategoriesUrl = `${environment.settingsUrl}${this.communityName}/community/categories`;

        this.searchQueryUpdate
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.unsubscriber))
            .subscribe(() => {
                this.filterFn(this.treeModel, this.searchQuery);
            });
    }

    afterTreeInit(event): void {
        this.treeModel = event.treeModel;

        this.treeModel.doForAll((node: TreeNode) => {
            return (node.data['nodePath'] = this.initNodePath(node, node.data.name + ', '));
        });

        if (this.selectedCategoryIds.length) {
            this.selectedCategoryIds.forEach((categoryId) => {
                const someNode = event.treeModel.getNodeById(categoryId);
                if (this.ignoreDeactivate) {
                    someNode.data.ignoreDeactivate = true;
                }
                someNode.setActiveAndVisible(true);
            });
        }
    }

    cleanSearch(treeModel: TreeModel): void {
        this.searchQuery = '';
        treeModel.collapseAll();
    }

    close(): void {
        if (this.modalId) {
            this.modalService.hide(this.modalId);
        } else {
            this.modalRef.hide();
        }
        if (this.onClose) {
            this.onClose();
        }
    }

    deleteCategory(): void {
        this.onChangeCategory(null);
        this.close();
    }

    filterFn(treeModel: TreeModel, searchQuery: string): void {
        if (searchQuery) {
            searchQuery = searchQuery.toLowerCase();
            treeModel.collapseAll();

            this.customFilterCategories(treeModel, searchQuery);
        } else {
            this.treeModel.clearFilter();
            this.cleanSearch(this.treeModel);
        }
    }

    onChangeNode(event): void {
        if (event && event.node && this.selectedCategoryIds.indexOf(event.node.data.id) === -1) {
            if (this.isIgnoreUnselected && !event.node.data.isClickable) {
                return;
            }

            this.onChangeCategory(event.node.data);
            this.modalRef.hide();
        }
    }

    private customFilterCategories(treeModel: TreeModel, searchQuery: string): void {
        setTimeout(() => {
            treeModel.filterNodes((node: TreeNode) => {
                return this.searchInNodePath(node, searchQuery);
            }, false);
        }, 1);
        this.expandExactMatches(treeModel, searchQuery);
    }

    private expandExactMatches(treeModel: TreeModel, searchQuery: string): void {
        treeModel.collapseAll();
        treeModel.doForAll((node: TreeNode) => {
            if (node.data.name.toLowerCase().includes(searchQuery)) {
                node.data.isSelected = true;
                node.ensureVisible();
            } else {
                node.data.isSelected = false;
            }
        });
    }

    private initNodePath(node: TreeNode, parentString: string): string {
        if (node.parent.data.name === undefined) {
            return parentString.slice(0, -2);
        } else {
            return this.initNodePath(node.parent, parentString + node.parent.data.name + ', ');
        }
    }

    private searchInNodePath(node: TreeNode, searchQuery: string): boolean {
        return node.data.nodePath?.toLowerCase().includes(searchQuery);
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
